'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEntriesFilters } from '../../hooks/useEntriesFilters';

interface EntriesFiltersPanelProps {
  /** Condiciones iniciales desde query params */
  initialConditions?: FilterConditionOutput[];
  /** Callback cuando cambian los filtros */
  onChange?: (conditions: FilterConditionOutput[]) => void;
  /** Si mostrar el filtro de cliente (solo para tenant_3pl) */
  hasTeamData?: boolean;
  /** Clases CSS adicionales */
  className?: string;
}

/**
 * Componente EntriesFiltersPanel
 *
 * Panel de filtros para el modal/dropdown de filtros de Entries.
 * Layout en grid de 2 columnas que se adapta a diferentes tamaños.
 * Nota: El filtro de estado se maneja mediante tabs (EntryStatusToolbar)
 */
export const EntriesFiltersPanel = ({
  initialConditions = [],
  onChange,
  hasTeamData = false,
  className,
}: EntriesFiltersPanelProps) => {
  const { t } = useTranslation('merchandiseV3');

  const {
    filters,
    handleCreatedAtChange,
    handleEstimatedDeliveryAtChange,
    handleReferenceChange,
    handleTrackingCodeChange,
    handleClientChange,
    handleProductsChange,
    loadClientOptions,
    loadProductsForMultiSelect,
    clearAllFilters,
  } = useEntriesFilters({
    initialConditions,
    onChange,
    hasTeamData,
  });

  // Estado local para referencia (solo se aplica en blur o Enter)
  const [localReference, setLocalReference] = useState(filters.reference);

  // Estado local para tracking code (solo se aplica en blur o Enter)
  const [localTrackingCode, setLocalTrackingCode] = useState(filters.trackingCode);

  // Sincronizar estado local cuando cambie el filtro externo
  useEffect(() => {
    setLocalReference(filters.reference);
  }, [filters.reference]);

  useEffect(() => {
    setLocalTrackingCode(filters.trackingCode);
  }, [filters.trackingCode]);

  // Aplicar filtro de referencia
  const applyReferenceFilter = () => {
    if (localReference !== filters.reference) {
      handleReferenceChange(localReference);
    }
  };

  // Aplicar filtro de tracking code
  const applyTrackingCodeFilter = () => {
    if (localTrackingCode !== filters.trackingCode) {
      handleTrackingCodeChange(localTrackingCode);
    }
  };

  // Contar filtros activos (sin contar status que está en tabs)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.createdAt?.from && filters.createdAt?.to) count++;
    if (filters.estimatedDeliveryAt?.from && filters.estimatedDeliveryAt?.to) count++;
    if (filters.reference && filters.reference.trim()) count++;
    if (filters.trackingCode && filters.trackingCode.trim()) count++;
    if (filters.client.length > 0) count++;
    if (filters.products.length > 0) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      {/* SECCIÓN: FECHAS */}
      <FilterSection title={t('filters.dates', 'Fechas')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.createdAt', 'Fecha de creación')}>
            <DateRangePicker
              initialDateFrom={filters.createdAt?.from}
              initialDateTo={filters.createdAt?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder={t('filters.selectDate', 'Seleccionar...')}
              dataTestId="filter-created-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>

          <FilterField label={t('filters.estimatedDeliveryAt', 'Fecha estimada de entrega')}>
            <DateRangePicker
              initialDateFrom={filters.estimatedDeliveryAt?.from}
              initialDateTo={filters.estimatedDeliveryAt?.to}
              onUpdate={({ range }) => handleEstimatedDeliveryAtChange(range)}
              placeholder={t('filters.selectDate', 'Seleccionar...')}
              dataTestId="filter-estimated-delivery-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      {/* SECCIÓN: OTROS FILTROS */}
      <FilterSection title={t('filters.title', 'Filtros')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.reference', 'Referencia')}>
            <input
              type="text"
              value={localReference}
              onChange={(e) => setLocalReference(e.target.value)}
              onBlur={applyReferenceFilter}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyReferenceFilter();
                }
              }}
              placeholder={t('filters.searchReference', 'Buscar por referencia...')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-reference"
            />
          </FilterField>

          <FilterField label={t('filters.trackingCode', 'Código de seguimiento')}>
            <input
              type="text"
              value={localTrackingCode}
              onChange={(e) => setLocalTrackingCode(e.target.value)}
              onBlur={applyTrackingCodeFilter}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  applyTrackingCodeFilter();
                }
              }}
              placeholder={t('filters.searchTrackingCode', 'Buscar por código de seguimiento...')}
              className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              data-testid="filter-tracking-code"
            />
          </FilterField>

          <FilterField label={t('filters.products', 'Producto')}>
            <MultiSelect
              loadOptions={loadProductsForMultiSelect}
              value={filters.products}
              onValueChange={handleProductsChange}
              placeholder={t('filters.allProducts', 'Todos los productos')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              noResultsText={t('filters.notFound', 'Sin resultados')}
              searchPlaceholder={t('filters.searchProducts', 'Buscar productos...')}
              dataTestId="filter-products"
              triggerClassName="h-[38px]"
              pageSize={10}
            />
          </FilterField>

          {/* Filtro de cliente - Solo para tenant_3pl */}
          {hasTeamData && (
            <FilterField label={t('filters.client', 'Cliente')}>
              <MultiSelect
                loadOptions={loadClientOptions}
                value={filters.client}
                onValueChange={handleClientChange}
                placeholder={t('filters.allClients', 'Todos los clientes')}
                emptyText={t('filters.notFound', 'Sin resultados')}
                pageSize={40}
                dataTestId="filter-client"
                triggerClassName="h-[38px]"
              />
            </FilterField>
          )}
        </div>
      </FilterSection>

      {/* Footer con acciones */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            {t('filters.clear', 'Limpiar')} {activeFiltersCount}{' '}
            {activeFiltersCount === 1 ? t('filters.filter', 'filtro') : t('filters.filtersCount', 'filtros')}
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Componente auxiliar para sección de filtros con título
 */
interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
}

const FilterSection = ({ title, children }: FilterSectionProps) => (
  <div className="space-y-3">
    <h4 className="text-sm font-semibold text-foreground uppercase tracking-wider">{title}</h4>
    {children}
  </div>
);

/**
 * Componente auxiliar para cada campo de filtro con label
 */
interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
